import { defineSlotRecipe } from '@chakra-ui/react';
import { dialogSlotRecipe } from './Modal';
import { Colors, Radii, Shadows } from '../../types';

// Drawer reuses the dialog base and squares the content corners. `createSystem` deep-merges, so the
// backdrop/positioner CSS below is redundant; the size variant is not, since it replaces stock's scale.
const dlg = dialogSlotRecipe.base as Record<string, any>;

export const drawerSlotRecipe = defineSlotRecipe({
  slots: [
    'root',
    'backdrop',
    'positioner',
    'content',
    'title',
    'description',
    'header',
    'body',
    'footer',
    'closeTrigger',
  ],
  base: {
    header: { ...dlg.header },
    title: { ...dlg.title },
    closeTrigger: { ...dlg.closeTrigger },
    body: { ...dlg.body },
    content: {
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      width: '100%',
      outline: 0,
      maxH: '100dvh',
      bg: Colors.SURFACE_ELEVATED,
      boxShadow: Shadows.XL,
      borderRadius: Radii.NONE,
      '--dialog-z-index': 'zIndex.popover',
      zIndex: 'calc(var(--dialog-z-index) + var(--layer-index, 0))',
    },
    footer: { ...dlg.footer },
    positioner: {
      display: 'flex',
      width: '100dvw',
      height: '100dvh',
      position: 'fixed',
      left: 0,
      top: 0,
      overscrollBehaviorY: 'none',
      '--dialog-z-index': 'zIndex.popover',
      zIndex: 'calc(var(--dialog-z-index) + var(--layer-index, 0))',
    },
    backdrop: {
      position: 'fixed',
      left: 0,
      top: 0,
      w: '100dvw',
      h: '100dvh',
      bg: Colors['BLACKALPHA.300'],
      _dark: { bg: Colors['BLACKALPHA.600'] },
      '--dialog-z-index': 'zIndex.popover',
      zIndex: 'calc(var(--dialog-z-index) + var(--layer-index, 0) - 1)',
    },
  },
  variants: {
    // xs-xl keep v2's shifted drawer scale; the larger sizes map to their same-named maxW token.
    size: {
      xs: { content: { maxW: 'xs' } },
      sm: { content: { maxW: 'md' } },
      md: { content: { maxW: 'lg' } },
      lg: { content: { maxW: '2xl' } },
      xl: { content: { maxW: '4xl' } },
      '2xl': { content: { maxW: '2xl' } },
      '3xl': { content: { maxW: '3xl' } },
      '4xl': { content: { maxW: '4xl' } },
      '5xl': { content: { maxW: '5xl' } },
      '6xl': { content: { maxW: '6xl' } },
      full: { content: { maxW: '100dvw', h: '100dvh' } },
    },
    placement: {
      start: {
        positioner: { justifyContent: 'flex-start', alignItems: 'stretch' },
        content: {
          _open: {
            animationName: { base: 'slide-from-left-full, fade-in', _rtl: 'slide-from-right-full, fade-in' },
          },
          _closed: {
            animationName: { base: 'slide-to-left-full, fade-out', _rtl: 'slide-to-right-full, fade-out' },
          },
        },
      },
      end: {
        positioner: { justifyContent: 'flex-end', alignItems: 'stretch' },
        content: {
          _open: {
            animationName: { base: 'slide-from-right-full, fade-in', _rtl: 'slide-from-left-full, fade-in' },
          },
          _closed: {
            animationName: { base: 'slide-to-right-full, fade-out', _rtl: 'slide-to-left-full, fade-out' },
          },
        },
      },
      top: {
        positioner: { justifyContent: 'stretch', alignItems: 'flex-start' },
        content: {
          maxW: '100%',
          _open: { animationName: 'slide-from-top-full, fade-in' },
          _closed: { animationName: 'slide-to-top-full, fade-out' },
        },
      },
      bottom: {
        positioner: { justifyContent: 'stretch', alignItems: 'flex-end' },
        content: {
          maxW: '100%',
          _open: { animationName: 'slide-from-bottom-full, fade-in' },
          _closed: { animationName: 'slide-to-bottom-full, fade-out' },
        },
      },
    },
  },
  defaultVariants: {
    size: 'xs',
    placement: 'end',
  },
});
